import { useState, useEffect } from "react";

const CustomSoftwareTotalCostCalculator = () => {
  // State for project size
  const [projectSize, setProjectSize] = useState("medium");

  // State for development costs
  const [developmentCosts, setDevelopmentCosts] = useState({
    devCost: 1750000,
    discoveryCost: 175000,
    designCost: 250000,
    qaCost: 175000,
    deploymentCost: 100000,
  });

  // State for recurring costs
  const [recurringCosts, setRecurringCosts] = useState({
    hosting: 100000,
    maintenance: 200000,
    thirdParty: 50000,
    support: 100000,
  });

  // State for additional costs
  const [additionalCosts, setAdditionalCosts] = useState({
    training: 50000,
    migration: 0,
    contingency: 15,
  });

  // State for results
  const [results, setResults] = useState({
    showResults: false,
    total3Year: 0,
    year1: 0,
    year2: 0,
    year3: 0,
    breakdown: [],
    metrics: {},
  });

  // Project size estimates (updated with better ranges based on images)
  const sizeEstimates = {
    micro: {
      label: "Micro (₹2-5L)",
      dev: 350000,
      discovery: 35000,
      design: 50000,
      qa: 35000,
      deployment: 25000,
    },
    small: {
      label: "Small (₹5-10L)",
      dev: 750000,
      discovery: 75000,
      design: 100000,
      qa: 75000,
      deployment: 50000,
    },
    medium: {
      label: "Medium (₹10-25L)",
      dev: 1750000,
      discovery: 175000,
      design: 250000,
      qa: 175000,
      deployment: 100000,
    },
    large: {
      label: "Large (₹25-50L)",
      dev: 3750000,
      discovery: 375000,
      design: 500000,
      qa: 375000,
      deployment: 200000,
    },
    enterprise: {
      label: "Enterprise (₹50L+)",
      dev: 7500000,
      discovery: 750000,
      design: 1000000,
      qa: 750000,
      deployment: 400000,
    },
    custom: {
      label: "Custom Amount",
      dev: 0,
      discovery: 0,
      design: 0,
      qa: 0,
      deployment: 0,
    },
  };

  // Calculate percentages automatically when dev cost changes
  useEffect(() => {
    if (projectSize !== "custom") {
      const calculatePercentages = () => {
        const dev = developmentCosts.devCost;
        setDevelopmentCosts(prev => ({
          ...prev,
          discoveryCost: Math.round(dev * 0.1), // 10% of dev
          designCost: Math.round(dev * 0.15), // 15% of dev
          qaCost: Math.round(dev * 0.1), // 10% of dev
          deploymentCost: Math.round(dev * 0.06), // 6% of dev
        }));
        
        // Update maintenance (15% of dev cost)
        setRecurringCosts(prev => ({
          ...prev,
          maintenance: Math.round(dev * 0.15),
        }));
      };
      
      calculatePercentages();
    }
  }, [developmentCosts.devCost, projectSize]);

  // Handle project size change
  const handleProjectSizeChange = (size) => {
    setProjectSize(size);

    if (size !== "custom") {
      const estimate = sizeEstimates[size];
      setDevelopmentCosts({
        devCost: estimate.dev,
        discoveryCost: Math.round(estimate.dev * 0.1), // 10% of dev
        designCost: Math.round(estimate.dev * 0.15), // 15% of dev
        qaCost: Math.round(estimate.dev * 0.1), // 10% of dev
        deploymentCost: Math.round(estimate.dev * 0.06), // 6% of dev
      });

      // Update maintenance (15% of dev cost)
      setRecurringCosts((prev) => ({
        ...prev,
        maintenance: Math.round(estimate.dev * 0.15),
      }));
    } else {
      // Reset to 0 for custom but keep current values for editing
      setDevelopmentCosts({
        devCost: developmentCosts.devCost || 0,
        discoveryCost: developmentCosts.discoveryCost || 0,
        designCost: developmentCosts.designCost || 0,
        qaCost: developmentCosts.qaCost || 0,
        deploymentCost: developmentCosts.deploymentCost || 0,
      });
    }
  };

  // Handle input changes
  const handleDevelopmentCostChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setDevelopmentCosts((prev) => ({
      ...prev,
      [field]: numValue,
    }));
    
    // If dev cost is changed and not in custom mode, update percentages
    if (field === "devCost" && projectSize !== "custom") {
      setDevelopmentCosts(prev => ({
        ...prev,
        devCost: numValue,
        discoveryCost: Math.round(numValue * 0.1),
        designCost: Math.round(numValue * 0.15),
        qaCost: Math.round(numValue * 0.1),
        deploymentCost: Math.round(numValue * 0.06),
      }));
      
      setRecurringCosts(prev => ({
        ...prev,
        maintenance: Math.round(numValue * 0.15),
      }));
    }
  };

  const handleRecurringCostChange = (field, value) => {
    setRecurringCosts((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  const handleAdditionalCostChange = (field, value) => {
    setAdditionalCosts((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return "₹" + (amount / 10000000).toFixed(1) + "Cr";
    } else if (amount >= 100000) {
      return "₹" + (amount / 100000).toFixed(1) + "L";
    }
    return "₹" + amount.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  };

  // Calculate total cost
  const calculateTotalCost = () => {
    // Destructure all costs
    const { devCost, discoveryCost, designCost, qaCost, deploymentCost } =
      developmentCosts;
    const { hosting, maintenance, thirdParty, support } = recurringCosts;
    const { training, migration, contingency } = additionalCosts;

    // Calculate totals
    const totalOneTime =
      devCost +
      discoveryCost +
      designCost +
      qaCost +
      deploymentCost +
      training +
      migration;
    const annualRecurring = hosting + maintenance + thirdParty + support;

    // Apply contingency
    const contingencyAmount = (totalOneTime * contingency) / 100;

    // Calculate year-by-year
    const year1 = totalOneTime + contingencyAmount + annualRecurring;
    const year2 = annualRecurring;
    const year3 = annualRecurring;

    const total3Year = year1 + year2 + year3;

    // Calculate metrics
    const monthlyAverage = Math.round(total3Year / 36);
    const developmentPercent = Math.round((totalOneTime / total3Year) * 100);
    const recurringPercent = Math.round(
      ((annualRecurring * 3) / total3Year) * 100,
    );

    // Build breakdown (matching image structure)
    const breakdown = [
      { category: "Development Cost", amount: devCost },
      { category: "Discovery & Planning (10%)", amount: discoveryCost },
      { category: "Design & UI/UX (15%)", amount: designCost },
      { category: "Testing & QA (10%)", amount: qaCost },
      { category: "Deployment & Setup (6%)", amount: deploymentCost },
      { category: "Training & Documentation", amount: training },
      ...(migration > 0
        ? [{ category: "Data Migration", amount: migration }]
        : []),
      {
        category: `Contingency Buffer (${contingency}%)`,
        amount: contingencyAmount,
      },
      {
        category: "Total One-Time Costs",
        amount: totalOneTime + contingencyAmount,
        isTotal: true,
      },
      { category: "Hosting & Infrastructure (Year 1)", amount: hosting },
      { category: "Maintenance & Updates (Year 1)", amount: maintenance },
      { category: "Third-Party Services (Year 1)", amount: thirdParty },
      { category: "Support & Monitoring (Year 1)", amount: support },
      {
        category: "Total Annual Recurring",
        amount: annualRecurring,
        isTotal: true,
      },
      { category: "Total 3-Year Recurring", amount: annualRecurring * 3, isTotal: true },
    ];

    // Set results
    setResults({
      showResults: true,
      total3Year,
      year1,
      year2,
      year3,
      breakdown,
      metrics: {
        monthlyAverage,
        developmentPercent,
        recurringPercent,
        annualRecurring,
        year1: year1,
      },
    });

    // Scroll to results
    setTimeout(() => {
      const resultsElement = document.getElementById("cost-results");
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // Reset calculator
  const resetCalculator = () => {
    setProjectSize("medium");
    setDevelopmentCosts({
      devCost: 1750000,
      discoveryCost: 175000,
      designCost: 250000,
      qaCost: 175000,
      deploymentCost: 100000,
    });
    setRecurringCosts({
      hosting: 100000,
      maintenance: 200000,
      thirdParty: 50000,
      support: 100000,
    });
    setAdditionalCosts({
      training: 50000,
      migration: 0,
      contingency: 15,
    });
    setResults({
      showResults: false,
      total3Year: 0,
      year1: 0,
      year2: 0,
      year3: 0,
      breakdown: [],
      metrics: {},
    });
  };

  // Add number input fields for better precision
  const NumberInput = ({ value, onChange, min = 0, max = 100000000, step = 10000 }) => (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      min={min}
      max={max}
      step={step}
      className="number-input"
      style={{
        marginTop: '8px',
        padding: '8px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        width: '100%',
        fontSize: '14px'
      }}
    />
  );

  return (
    <div className="cost-calculator-page">
      {/* Hero Section - Matching home page design */}
      <section className="calculator-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">💵</span>
              <span className="badge-text">COST CALCULATOR</span>
            </div>
            <h1 className="hero-title">
              Custom Software{" "}
              <span className="highlight">Total Cost Calculator</span>
            </h1>
            <p className="hero-subtitle">
              Calculate complete 3-year investment for your custom software
              project. No hidden surprises!
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-value">3-Year</div>
                <div className="stat-label">Total Cost View</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">40+</div>
                <div className="stat-label">Cost Categories</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">98%</div>
                <div className="stat-label">Accuracy Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Calculator */}
      <div className="container main-calculator">
        {/* Info Banner */}
        <div className="info-banner">
          <div className="info-icon">📊</div>
          <div className="info-content">
            <h3>Complete Cost Transparency</h3>
            <p>
              This calculator includes ALL costs: development, infrastructure,
              maintenance, support, training, and contingency.
            </p>
          </div>
        </div>

        {/* Calculator Card */}
        <div className="calculator-card">
          {/* Project Size Selection */}
          <div className="section">
            <div className="section-header">
              <div className="section-icon">📐</div>
              <div>
                <h3>Select Your Project Size</h3>
                <p>Choose a preset or enter custom values</p>
              </div>
            </div>

            <div className="project-size-selector">
              {Object.entries(sizeEstimates).map(([key, estimate]) => (
                <button
                  key={key}
                  className={`size-option ${projectSize === key ? "active" : ""}`}
                  onClick={() => handleProjectSizeChange(key)}
                >
                  <span className="size-label">{estimate.label}</span>
                  {key !== "custom" && (
                    <span className="size-range">
                      {formatCurrency(estimate.dev)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Development Costs */}
          <div className="section">
            <div className="section-header">
              <div className="section-icon">🛠️</div>
              <div>
                <h3>Development Costs (One-Time)</h3>
                <p>Initial investment for building your software</p>
              </div>
            </div>

            <div className="input-grid">
              <div className="input-group">
                <div>
                  <span className="label-text">Development Cost</span>
                  <span className="label-value">
                    {formatCurrency(developmentCosts.devCost)}
                  </span>
                </div>
                <NumberInput
                  value={developmentCosts.devCost}
                  onChange={(value) => handleDevelopmentCostChange("devCost", value)}
                  min={100000}
                  max={10000000}
                  step={50000}
                />
                <input
                  type="range"
                  min="100000"
                  max="10000000"
                  step="50000"
                  value={developmentCosts.devCost}
                  onChange={(e) =>
                    handleDevelopmentCostChange("devCost", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="slider-ticks">
                  <span>₹1L</span>
                  <span>₹50L</span>
                  <span>₹1Cr</span>
                </div>
                <div className="help-text">Core software development</div>
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Discovery & Planning</span>
                  <br />
                  <span className="label-value">
                    {formatCurrency(developmentCosts.discoveryCost)}
                  </span>
                </div>
                <div className="percentage-tag">10% of dev</div>
                <NumberInput
                  value={developmentCosts.discoveryCost}
                  onChange={(value) => handleDevelopmentCostChange("discoveryCost", value)}
                  min={0}
                  max={developmentCosts.devCost * 0.2}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max={developmentCosts.devCost * 0.2}
                  step="10000"
                  value={developmentCosts.discoveryCost}
                  onChange={(e) =>
                    handleDevelopmentCostChange("discoveryCost", e.target.value)
                  }
                  className="range-slider"
                />
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Design & UI/UX</span>
                  <span className="label-value">
                    {formatCurrency(developmentCosts.designCost)}
                  </span>
                </div>
                <div className="percentage-tag">15% of dev</div>
                <NumberInput
                  value={developmentCosts.designCost}
                  onChange={(value) => handleDevelopmentCostChange("designCost", value)}
                  min={0}
                  max={developmentCosts.devCost * 0.25}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max={developmentCosts.devCost * 0.25}
                  step="10000"
                  value={developmentCosts.designCost}
                  onChange={(e) =>
                    handleDevelopmentCostChange("designCost", e.target.value)
                  }
                  className="range-slider"
                />
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Testing & QA</span>
                  <span className="label-value">
                    {formatCurrency(developmentCosts.qaCost)}
                  </span>
                </div>
                <div className="percentage-tag">10% of dev</div>
                <NumberInput
                  value={developmentCosts.qaCost}
                  onChange={(value) => handleDevelopmentCostChange("qaCost", value)}
                  min={0}
                  max={developmentCosts.devCost * 0.2}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max={developmentCosts.devCost * 0.2}
                  step="10000"
                  value={developmentCosts.qaCost}
                  onChange={(e) =>
                    handleDevelopmentCostChange("qaCost", e.target.value)
                  }
                  className="range-slider"
                />
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Deployment & Setup</span>
                  <span className="label-value">
                    {formatCurrency(developmentCosts.deploymentCost)}
                  </span>
                </div>
                <div className="percentage-tag">6% of dev</div>
                <NumberInput
                  value={developmentCosts.deploymentCost}
                  onChange={(value) => handleDevelopmentCostChange("deploymentCost", value)}
                  min={0}
                  max={developmentCosts.devCost * 0.15}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max={developmentCosts.devCost * 0.15}
                  step="10000"
                  value={developmentCosts.deploymentCost}
                  onChange={(e) =>
                    handleDevelopmentCostChange(
                      "deploymentCost",
                      e.target.value,
                    )
                  }
                  className="range-slider"
                />
              </div>
            </div>
          </div>

          {/* Recurring Annual Costs */}
          <div className="section">
            <div className="section-header">
              <div className="section-icon">🔄</div>
              <div>
                <h3>Recurring Annual Costs</h3>
                <p>Ongoing expenses for 3 years</p>
              </div>
            </div>

            <div className="input-grid">
              <div className="input-group">
                <div>
                  <span className="label-text">Hosting & Infrastructure</span>
                  <span className="label-value">
                    {formatCurrency(recurringCosts.hosting)}/year
                  </span>
                </div>
                <NumberInput
                  value={recurringCosts.hosting}
                  onChange={(value) => handleRecurringCostChange("hosting", value)}
                  min={0}
                  max={500000}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max="500000"
                  step="10000"
                  value={recurringCosts.hosting}
                  onChange={(e) =>
                    handleRecurringCostChange("hosting", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="slider-ticks">
                  <span>₹0</span>
                  <span>₹2.5L</span>
                  <span>₹5L</span>
                </div>
                <div className="help-text">Cloud hosting, servers, CDN</div>
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Maintenance & Updates</span>
                  <span className="label-value">
                    {formatCurrency(recurringCosts.maintenance)}/year
                  </span>
                </div>
                <div className="percentage-tag">15% of dev</div>
                <NumberInput
                  value={recurringCosts.maintenance}
                  onChange={(value) => handleRecurringCostChange("maintenance", value)}
                  min={0}
                  max={developmentCosts.devCost * 0.3}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max={developmentCosts.devCost * 0.3}
                  step="10000"
                  value={recurringCosts.maintenance}
                  onChange={(e) =>
                    handleRecurringCostChange("maintenance", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="help-text">Bug fixes, minor updates</div>
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Third-Party Services</span>
                  <span className="label-value">
                    {formatCurrency(recurringCosts.thirdParty)}/year
                  </span>
                </div>
                <NumberInput
                  value={recurringCosts.thirdParty}
                  onChange={(value) => handleRecurringCostChange("thirdParty", value)}
                  min={0}
                  max={200000}
                  step={5000}
                />
                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="5000"
                  value={recurringCosts.thirdParty}
                  onChange={(e) =>
                    handleRecurringCostChange("thirdParty", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="help-text">APIs, payment gateways, tools</div>
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Support & Monitoring</span>
                  <span className="label-value">
                    {formatCurrency(recurringCosts.support)}/year
                  </span>
                </div>
                <NumberInput
                  value={recurringCosts.support}
                  onChange={(value) => handleRecurringCostChange("support", value)}
                  min={0}
                  max={300000}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max="300000"
                  step="10000"
                  value={recurringCosts.support}
                  onChange={(e) =>
                    handleRecurringCostChange("support", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="help-text">
                  Technical support, monitoring tools
                </div>
              </div>
            </div>
          </div>

          {/* Additional One-Time Costs */}
          <div className="section">
            <div className="section-header">
              <div className="section-icon">➕</div>
              <div>
                <h3>Additional One-Time Costs</h3>
                <p>Setup and contingency expenses</p>
              </div>
            </div>

            <div className="input-grid">
              <div className="input-group">
                <div>
                  <span className="label-text">Training & Documentation</span>
                  <span className="label-value">
                    {formatCurrency(additionalCosts.training)}
                  </span>
                </div>
                <NumberInput
                  value={additionalCosts.training}
                  onChange={(value) => handleAdditionalCostChange("training", value)}
                  min={0}
                  max={200000}
                  step={5000}
                />
                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="5000"
                  value={additionalCosts.training}
                  onChange={(e) =>
                    handleAdditionalCostChange("training", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="slider-ticks">
                  <span>₹0</span>
                  <span>₹1L</span>
                  <span>₹2L</span>
                </div>
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Data Migration</span>
                  <span className="label-value">
                    {formatCurrency(additionalCosts.migration)}
                  </span>
                </div>
                <NumberInput
                  value={additionalCosts.migration}
                  onChange={(value) => handleAdditionalCostChange("migration", value)}
                  min={0}
                  max={300000}
                  step={10000}
                />
                <input
                  type="range"
                  min="0"
                  max="300000"
                  step="10000"
                  value={additionalCosts.migration}
                  onChange={(e) =>
                    handleAdditionalCostChange("migration", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="help-text">If moving from existing system</div>
              </div>

              <div className="input-group">
                <div>
                  <span className="label-text">Contingency Buffer</span>
                  <span className="label-value">
                    {additionalCosts.contingency}%
                  </span>
                </div>
                <NumberInput
                  value={additionalCosts.contingency}
                  onChange={(value) => handleAdditionalCostChange("contingency", value)}
                  min={0}
                  max={30}
                  step={1}
                />
                <input
                  type="range"
                  min="0"
                  max="30"
                  step="1"
                  value={additionalCosts.contingency}
                  onChange={(e) =>
                    handleAdditionalCostChange("contingency", e.target.value)
                  }
                  className="range-slider"
                />
                <div className="slider-ticks">
                  <span>0%</span>
                  <span>15%</span>
                  <span>30%</span>
                </div>
                <div className="help-text">
                  Recommended: 10-20% for unexpected costs
                </div>
              </div>
            </div>
          </div>

          {/* Calculate Button */}
          <div className="calculate-section">
            <button className="calculate-btn" onClick={calculateTotalCost}>
              <span className="btn-icon">💰</span>
              <span className="btn-text">Calculate Total 3-Year Cost</span>
              <span className="btn-arrow">→</span>
            </button>
            <p className="calculate-note">
              Get a complete breakdown of all costs over 3 years
            </p>
            <button 
              className="reset-btn" 
              onClick={resetCalculator}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                marginTop: '12px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Reset Calculator
            </button>
          </div>
        </div>

        {/* Results Section */}
        {results.showResults && (
          <div className="results-section" id="cost-results">
            {/* Results Header */}
            <div className="results-header">
              <h2>Your 3-Year Custom Software Investment</h2>
              <p>Complete cost breakdown and projections</p>
            </div>

            {/* Total Cost Highlight */}
            <div className="total-cost-highlight">
              <div className="total-cost-content">
                <div className="total-cost-icon">💵</div>
                <div className="total-cost-text">
                  <h3>Total 3-Year Investment</h3>
                  <p>Complete cost of ownership</p>
                </div>
                <div className="total-cost-amount">
                  {formatCurrency(results.total3Year)}
                </div>
              </div>
            </div>

            {/* Year-by-Year Breakdown */}
            <div className="year-breakdown-grid">
              <div className="year-card">
                <div className="year-header">
                  <div className="year-number">1</div>
                  <h4>Year 1</h4>
                </div>
                <div className="year-amount">
                  {formatCurrency(results.year1)}
                </div>
                <div className="year-note">
                  Includes development + first year recurring
                </div>
              </div>

              <div className="year-card">
                <div className="year-header">
                  <div className="year-number">2</div>
                  <h4>Year 2</h4>
                </div>
                <div className="year-amount">
                  {formatCurrency(results.year2)}
                </div>
                <div className="year-note">Recurring costs only</div>
              </div>

              <div className="year-card">
                <div className="year-header">
                  <div className="year-number">3</div>
                  <h4>Year 3</h4>
                </div>
                <div className="year-amount">
                  {formatCurrency(results.year3)}
                </div>
                <div className="year-note">Recurring costs only</div>
              </div>
            </div>

            {/* Detailed Cost Breakdown */}
            <div className="cost-breakdown-section">
              <div className="section-header">
                <div className="section-icon">📋</div>
                <div>
                  <h3>Detailed Cost Breakdown</h3>
                  <p>Complete itemized list of all costs</p>
                </div>
              </div>

              <div className="breakdown-table">
                <div className="table-header">
                  <div className="table-col category">Category</div>
                  <div className="table-col amount">Amount</div>
                </div>

                {results.breakdown.map((item, index) => (
                  <div
                    key={index}
                    className={`breakdown-row ${item.isTotal ? "total-row" : ""}`}
                  >
                    <div className="table-col category">{item.category}</div>
                    <div className="table-col amount">
                      {formatCurrency(item.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Metrics */}
            <div className="metrics-section">
              <div className="section-header">
                <div className="section-icon">📊</div>
                <div>
                  <h3>Key Metrics & Insights</h3>
                  <p>Important numbers to consider</p>
                </div>
              </div>

              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-icon">📅</div>
                  <div className="metric-content">
                    <div className="metric-value">
                      {formatCurrency(results.metrics.monthlyAverage)}
                    </div>
                    <div className="metric-label">Average Monthly Cost</div>
                    <div className="metric-note">Over 36 months</div>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">🏗️</div>
                  <div className="metric-content">
                    <div className="metric-value">
                      {results.metrics.developmentPercent}%
                    </div>
                    <div className="metric-label">Development % of Total</div>
                    <div className="metric-note">One-time investment</div>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">🔄</div>
                  <div className="metric-content">
                    <div className="metric-value">
                      {results.metrics.recurringPercent}%
                    </div>
                    <div className="metric-label">Recurring % of Total</div>
                    <div className="metric-note">Ongoing expenses</div>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">💼</div>
                  <div className="metric-content">
                    <div className="metric-value">
                      {formatCurrency(results.metrics.annualRecurring)}
                    </div>
                    <div className="metric-label">Annual Recurring Cost</div>
                    <div className="metric-note">Years 2 & 3</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-section">
              <div className="action-buttons">
                <button
                  className="action-btn primary"
                  onClick={() => window.open("/contact", "_blank")}
                >
                  <span>Get Detailed Proposal</span>
                  <span>📋</span>
                </button>
                <button
                  className="action-btn secondary"
                  onClick={resetCalculator}
                >
                  <span>Recalculate</span>
                  <span>↻</span>
                </button>
              </div>
              <p className="action-note">
                Want a personalized quote? Our experts will review these numbers
                and provide exact estimates.
              </p>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .cost-calculator-page {
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Hero Section - Matching home page */
        .calculator-hero {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          padding: 80px 0 60px;
          position: relative;
          overflow: hidden;
        }

        .calculator-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(
              circle at 20% 80%,
              rgba(255, 215, 0, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 255, 255, 0.05) 0%,
              transparent 50%
            );
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
          padding: 0 20px;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 8px 16px;
          border-radius: 50px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .badge-icon {
          font-size: 20px;
        }

        .badge-text {
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 1px;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 20px;
          word-wrap: break-word;
        }

        .highlight {
          background: linear-gradient(90deg, #ffd700 0%, #ffec8b 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          max-width: 600px;
          margin: 0 auto 40px;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
        }

        .stat-item {
          text-align: center;
          min-width: 120px;
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffd700;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          width: 100%;
          box-sizing: border-box;
        }

        /* Main Calculator */
        .main-calculator {
          padding: 60px 20px;
        }

        /* Info Banner */
        .info-banner {
          background: linear-gradient(
            135deg,
            rgba(10, 36, 88, 0.05) 0%,
            rgba(10, 36, 88, 0.02) 100%
          );
          border: 1px solid rgba(10, 36, 88, 0.1);
          border-radius: 16px;
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 40px;
        }

        .info-icon {
          font-size: 32px;
          width: 60px;
          height: 60px;
          background: rgba(10, 36, 88, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .info-content h3 {
          color: #0a2458;
          margin-bottom: 8px;
          font-size: 1.25rem;
        }

        .info-content p {
          color: #0a2458;
          line-height: 1.6;
          margin: 0;
        }

        /* Calculator Card */
        .calculator-card {
          background: white;
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(10, 36, 88, 0.1);
          overflow: hidden;
          margin-bottom: 60px;
        }

        /* Sections */
        .section {
          padding: 40px;
          border-bottom: 1px solid #f0f2f5;
        }

        .section:last-child {
          border-bottom: none;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 32px;
        }

        .section-icon {
          font-size: 32px;
          width: 60px;
          height: 60px;
          background: rgba(10, 36, 88, 0.1);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .section-header h3 {
          font-size: 1.5rem;
          color: #0a2458;
          margin-bottom: 4px;
        }

        .section-header p {
          color: #0a2458;
          margin: 0;
        }

        /* Project Size Selector */
        .project-size-selector {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-top: 24px;
        }

        .size-option {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .size-option:hover {
          border-color: #0a2458;
          transform: translateY(-2px);
        }

        .size-option.active {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          border-color: #0a2458;
          color: white;
        }

        .size-option.active .size-label,
        .size-option.active .size-range {
          color: white;
        }

        .size-label {
          font-weight: 600;
          color: #0a2458;
          text-align: center;
        }

        .size-range {
          font-size: 0.875rem;
          color: #0a2458;
        }

        /* Input Grid */
        .input-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 32px;
        }

        /* Input Group */
        .input-group {
          background: #f8fafc;
          border-radius: 16px;
          padding: 24px;
          transition: all 0.3s ease;
        }

        .input-group:hover {
          background: #f0f7ff;
          transform: translateY(-2px);
        }

        .input-group label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .label-text {
          font-weight: 600;
          color: #0a2458;
          font-size: 1.125rem;
          flex: 1;
        }

        .label-value {
          background: white;
          padding: 6px 12px;
          border-radius: 8px;
          font-weight: 600;
          color: #1a3b8a;
          font-size: 0.875rem;
          border: 1px solid #e2e8f0;
        }

        .percentage-tag {
          background: #ffd700;
          color: #0a2458;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-block;
          margin-bottom: 12px;
        }

        .range-slider {
          width: 100%;
          height: 8px;
          background: linear-gradient(to right, #e2e8f0, #1a3b8a);
          border-radius: 4px;
          outline: none;
          -webkit-appearance: none;
          margin: 16px 0;
        }

        .range-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 24px;
          height: 24px;
          background: #0a2458;
          border-radius: 50%;
          cursor: pointer;
          border: 4px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          transition: all 0.2s ease;
        }

        .range-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          background: #1a3b8a;
        }

        .slider-ticks {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          color: #94a3b8;
          font-size: 0.75rem;
        }

        .help-text {
          font-size: 0.875rem;
          color: #0a2458;
          margin-top: 8px;
          line-height: 1.4;
        }

        /* Calculate Section */
        .calculate-section {
          padding: 40px;
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          text-align: center;
        }

        .calculate-btn {
          background: white;
          color: #0a2458;
          border: none;
          padding: 20px 40px;
          font-size: 1.25rem;
          font-weight: 600;
          border-radius: 16px;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 16px;
        }

        .calculate-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }

        .calculate-note {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.875rem;
        }

        /* Results Section */
        .results-section {
          animation: slideIn 0.6s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .results-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .results-header h2 {
          font-size: 2.5rem;
          color: #0a2458;
          margin-bottom: 12px;
        }

        .results-header p {
          color: #0a2458;
          font-size: 1.125rem;
        }

        /* Total Cost Highlight */
        .total-cost-highlight {
          background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
          border-radius: 20px;
          padding: 40px;
          margin-bottom: 60px;
          color: white;
        }

        .total-cost-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
        }

        .total-cost-icon {
          font-size: 48px;
          flex-shrink: 0;
        }

        .total-cost-text {
          flex: 1;
          min-width: 200px;
        }

        .total-cost-text h3 {
          font-size: 1.5rem;
          margin-bottom: 4px;
        }

        .total-cost-text p {
          opacity: 0.9;
          margin: 0;
        }

        .total-cost-amount {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 700;
          flex-shrink: 0;
          white-space: nowrap;
        }

        /* Year Breakdown */
        .year-breakdown-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
          margin-bottom: 60px;
        }

        .year-card {
          background: white;
          border-radius: 16px;
          padding: 32px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s ease;
        }

        .year-card:hover {
          transform: translateY(-5px);
        }

        .year-header {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .year-number {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .year-header h4 {
          font-size: 1.25rem;
          color: #0a2458;
          margin: 0;
        }

        .year-amount {
          font-size: 2.5rem;
          font-weight: 700;
          color: #0a2458;
          margin: 16px 0;
        }

        .year-note {
          color: #0a2458;
          font-size: 0.875rem;
        }

        /* Cost Breakdown Section */
        .cost-breakdown-section {
          margin-bottom: 60px;
        }

        .breakdown-table {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
        }

        .table-header {
          display: grid;
          grid-template-columns: 1fr auto;
          background: #f8fafc;
          padding: 20px 24px;
          border-bottom: 2px solid #e2e8f0;
        }

        .table-col {
          font-weight: 600;
          color: #0a2458;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .breakdown-row {
          display: grid;
          grid-template-columns: 1fr auto;
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
        }

        .breakdown-row:last-child {
          border-bottom: none;
        }

        .breakdown-row.total-row {
          background: #f0f7ff;
          font-weight: 700;
          border-top: 2px solid #0a2458;
        }

        .breakdown-row .category {
          color: #0a2458;
          font-size: 0.95rem;
        }

        .breakdown-row.total-row .category {
          color: #0a2458;
        }

        .breakdown-row .amount {
          color: #0a2458;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .breakdown-row.total-row .amount {
          color: #0a2458;
          font-size: 1.1rem;
        }

        /* Metrics Section */
        .metrics-section {
          margin-bottom: 60px;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-top: 32px;
        }

        .metric-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
          transition: transform 0.3s ease;
        }

        .metric-card:hover {
          transform: translateY(-5px);
        }

        .metric-icon {
          font-size: 32px;
          width: 60px;
          height: 60px;
          background: rgba(10, 36, 88, 0.1);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .metric-content {
          flex: 1;
        }

        .metric-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #0a2458;
          margin-bottom: 4px;
        }

        .metric-label {
          font-size: 0.875rem;
          color: #0a2458;
          margin-bottom: 4px;
        }

        .metric-note {
          font-size: 0.75rem;
          color: #94a3b8;
        }

        /* Action Section */
        .action-section {
          text-align: center;
          padding: 40px;
          background: linear-gradient(
            135deg,
            rgba(10, 36, 88, 0.05) 0%,
            rgba(10, 36, 88, 0.02) 100%
          );
          border-radius: 20px;
        }

        .action-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .action-btn {
          padding: 18px 32px;
          border-radius: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
          font-size: 1rem;
        }

        .action-btn.primary {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
        }

        .action-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(10, 36, 88, 0.3);
        }

        .action-btn.secondary {
          background: transparent;
          color: #0a2458;
          border: 2px solid #0a2458;
        }

        .action-btn.secondary:hover {
          background: rgba(10, 36, 88, 0.05);
        }

        .action-note {
          color: #0a2458;
          font-size: 0.875rem;
          max-width: 500px;
          margin: 0 auto;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero-stats {
            gap: 20px;
          }

          .stat-item {
            min-width: 100px;
          }

          .stat-value {
            font-size: 2rem;
          }

          .section {
            padding: 30px 20px;
          }

          .input-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .project-size-selector {
            grid-template-columns: 1fr;
          }

          .total-cost-content {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }

          .year-breakdown-grid {
            grid-template-columns: 1fr;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .calculator-hero {
            padding: 60px 0 40px;
          }

          .hero-title {
            font-size: 2rem;
          }

          .hero-subtitle {
            font-size: 1.125rem;
          }

          .total-cost-highlight {
            padding: 30px 20px;
          }

          .total-cost-amount {
            font-size: 2rem;
          }

          .year-amount {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomSoftwareTotalCostCalculator;